<template>
  <div v-if="isOpen" class="fitbase-modal-overlay" @click.self="closeModal">
    <div class="fitbase-modal-content">
      <div class="fitbase-modal-header">
        <h3 class="fitbase-modal-title">Вход</h3>
        <button class="fitbase-modal-close" type="button" @click="closeModal">
          <img :src="closeIcon" alt="Закрыть" class="fitbase-modal-close-icon" />
        </button>
      </div>

      <div class="fitbase-modal-layout">
        <div class="fitbase-modal-main">
          <div v-if="step === 'phone'" class="fitbase-modal-body fitbase-phone-step">
            <h4 class="fitbase-step-title">Войти в личный кабинет</h4>

            <vue-tel-input
              :key="defaultCountry"
              v-model="phoneInput"
              class="fitbase-phone-input"
              :default-country="defaultCountry"
              :preferred-countries="preferredCountries"
              :input-options="phoneInputOptions"
              @validate="onPhoneValidate"
              @country-changed="onPhoneCountryChanged"
              @input="clearPhoneError"
            />

            <p v-if="errorText" class="fitbase-error-text">{{ errorText }}</p>

            <button
              type="button"
              class="fitbase-primary-button"
              :disabled="!canSubmitPhone || isSubmitting"
              @click="submitPhone"
            >
              Продолжить
            </button>

            <p class="fitbase-legal-text">
              <span>Продолжая, вы даёте </span>
              <a
                v-if="agreeParts.consent.href"
                :href="agreeParts.consent.href"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ agreeParts.consent.label }}
              </a>
              <span v-else>{{ agreeParts.consent.label }}</span>
              <span> на </span>
              <a
                v-if="agreeParts.personalData.href"
                :href="agreeParts.personalData.href"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ agreeParts.personalData.label }}
              </a>
              <span v-else>{{ agreeParts.personalData.label }}</span>
              <span>. Соглашаетесь с </span>
              <a
                v-if="agreeParts.offer.href"
                :href="agreeParts.offer.href"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ agreeParts.offer.label }}
              </a>
              <span v-else>{{ agreeParts.offer.label }}</span>
              <span>, </span>
              <a
                v-if="agreeParts.clubRules.href"
                :href="agreeParts.clubRules.href"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ agreeParts.clubRules.label }}
              </a>
              <span v-else>{{ agreeParts.clubRules.label }}</span>
              <span>.</span>
            </p>
          </div>

          <div v-else-if="step === 'code'" class="fitbase-modal-body fitbase-code-step">
            <h4 class="fitbase-step-title">Код из SMS</h4>

            <div class="fitbase-code-hint">
              <p class="fitbase-hint-text">Отправили на {{ formattedPhone }}</p>
            </div>

            <div class="fitbase-code-inputs">
              <input
                v-for="(_, index) in codeDigits"
                :key="index"
                :ref="(element) => setCodeInputRef(element, index)"
                v-model="codeDigits[index]"
                type="tel"
                inputmode="numeric"
                maxlength="1"
                autocomplete="one-time-code"
                class="fitbase-code-input"
                :class="{ filled: !!codeDigits[index] }"
                @input="onCodeInput(index, $event)"
                @keydown.backspace="onCodeBackspace(index, $event)"
                @paste="onCodePaste"
              />
            </div>

            <p v-if="errorText" class="fitbase-error-text">{{ errorText }}</p>

            <button
              type="button"
              class="fitbase-primary-button"
              :disabled="!canSubmitCode || isSubmitting"
              @click="submitCode"
            >
              Продолжить
            </button>

            <div class="fitbase-resend-block">
              <template v-if="secondsLeft > 0">
                <span class="fitbase-resend-text">Повторная отправка через: {{ secondsLeft }} сек</span>
              </template>
              <button
                v-else
                type="button"
                class="fitbase-resend-button"
                :disabled="isSubmitting"
                @click="resendCode"
              >
                Отправить повторно
              </button>
            </div>
          </div>

          <div v-else class="fitbase-modal-body fitbase-registration-step">
            <h4 class="fitbase-step-title">Регистрация</h4>

            <input
              v-model.trim="registrationForm.name"
              type="text"
              autocomplete="given-name"
              class="fitbase-text-input fitbase-text-input-left"
              placeholder="Имя"
              @input="clearPhoneError"
            />

            <input
              v-model.trim="registrationForm.surname"
              type="text"
              autocomplete="family-name"
              class="fitbase-text-input fitbase-text-input-left"
              placeholder="Фамилия"
              @input="clearPhoneError"
            />

            <Datepicker
              v-model="registrationForm.birthDate"
              model-type="yyyy-MM-dd"
              placeholder="Дата рождения"
              format="dd.MM.yyyy"
              locale="ru"
              cancel-text=""
              select-text="Применить"
              :enable-time-picker="false"
              :disabled-dates="disableFutureDates"
              auto-apply
            />

            <div class="fitbase-sex-buttons">
              <button
                type="button"
                class="fitbase-sex-button"
                :class="{ active: registrationForm.sex === 'мужской' }"
                @click="registrationForm.sex = 'мужской'"
              >
                Мужской
              </button>
              <button
                type="button"
                class="fitbase-sex-button"
                :class="{ active: registrationForm.sex === 'женский' }"
                @click="registrationForm.sex = 'женский'"
              >
                Женский
              </button>
            </div>

            <p v-if="errorText" class="fitbase-error-text">{{ errorText }}</p>

            <button
              type="button"
              class="fitbase-primary-button"
              :disabled="!canSubmitRegistration || isSubmitting"
              @click="submitRegistration"
            >
              Зарегистрироваться
            </button>
          </div>
        </div>

        <div class="fitbase-manufactured-by">
          <a href="https://fitbase.io" target="_blank" rel="noopener noreferrer" class="fitbase-manufactured-by-link">
            <img :src="workOnFitbaseImage" alt="Работает на FITBASE" class="fitbase-manufactured-by-image" />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css'
import { BookingAPI } from '@/widgets/booking/api'
import { clearStoredAuthPhone, clearStoredAuthToken, getRuntimeConfig, setStoredAuthPhone, setStoredAuthToken } from '@/widgets/booking/config/runtime'
import closeIcon from '@/widgets/booking/assets/img/icons/booking-close.svg'
import workOnFitbaseImage from '@/assets/img/work_on_fitbase.svg'

const FALLBACK_PREFERRED_COUNTRIES = ['RU', 'BY', 'KZ', 'UZ', 'KG', 'AM', 'AZ', 'TJ', 'GE', 'ID']

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['close', 'authorized'])

const step = ref('phone')
const phoneInput = ref('')
const phoneValue = ref('')
const phoneValid = ref(false)
const currentDialCode = ref('')
const phoneInputOptions = { placeholder: 'Ваш телефон' }
const codeDigits = ref(['', '', '', ''])
const errorText = ref('')
const isSubmitting = ref(false)
const secondsLeft = ref(60)
const timerId = ref(null)
const codeInputRefs = ref([])
const settings = ref({})
const pendingToken = ref('')
const hasTemporaryAuth = ref(false)
const registrationForm = ref({
  name: '',
  surname: '',
  birthDate: null,
  sex: ''
})

const defaultCountry = computed(() => {
  const code = settings.value?.widget?.defaultCountry
  return String(code || 'RU').toUpperCase()
})
const preferredCountries = computed(() => {
  const fromSettings = settings.value?.widget?.preferredCountries
  const list = Array.isArray(fromSettings) && fromSettings.length
    ? fromSettings.map((code) => String(code).toUpperCase())
    : FALLBACK_PREFERRED_COUNTRIES.slice()
  const priority = defaultCountry.value
  return [priority, ...list.filter((code) => code !== priority)]
})
const formattedPhone = computed(() => phoneValue.value || phoneInput.value)
const codeValue = computed(() => codeDigits.value.join(''))
const canSubmitPhone = computed(() => phoneValid.value && Boolean(phoneValue.value))
const canSubmitCode = computed(() => codeValue.value.length === 4 && codeDigits.value.every((digit) => /^\d$/.test(digit)))
const canSubmitRegistration = computed(() => {
  return Boolean(
    registrationForm.value.name
    && registrationForm.value.surname
    && registrationForm.value.birthDate
    && registrationForm.value.sex
  )
})
const agreeParts = computed(() => buildAgreeParts(settings.value))

watch(() => props.isOpen, (isOpen) => {
  if (!isOpen) {
    resetModal()
  }
})

watch(codeValue, (value) => {
  if (step.value !== 'code' || isSubmitting.value) {
    return
  }

  if (value.length === 4 && codeDigits.value.every((digit) => /^\d$/.test(digit))) {
    submitCode()
  }
})

watch(phoneInput, (val) => {
  if (typeof val === 'string' && val.charAt(0) === '8' && currentDialCode.value === '7') {
    phoneInput.value = `+7${val.slice(1)}`
  }
})

onMounted(async () => {
  settings.value = await BookingAPI.getSettings()
})

onBeforeUnmount(() => {
  stopTimer()
})

function onPhoneValidate(phone) {
  phoneValid.value = !!phone?.valid
  phoneValue.value = phoneValid.value
    ? `+${phone.countryCallingCode}${phone.nationalNumber}`
    : ''
}

function onPhoneCountryChanged(country) {
  currentDialCode.value = country?.dialCode || ''
}

function setCodeInputRef(element, index) {
  if (!(element instanceof HTMLInputElement)) {
    return
  }

  codeInputRefs.value[index] = element
}

function sanitizeExternalUrl(url) {
  if (!url || typeof url !== 'string') {
    return ''
  }

  try {
    const parsed = new URL(url, window.location.origin)

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return ''
    }

    return parsed.toString()
  } catch (error) {
    return ''
  }
}

function buildAgreeParts(payload = {}) {
  try {
    const modules = payload.modules || {}
    const consent = modules.consent_personal_data
    const personalData = modules.processing_personal_data
    const offer = modules.offer
    const clubRules = modules.club_rules

    return {
      consent: {
        label: 'согласие',
        href: consent?.enable === true ? sanitizeExternalUrl(consent.link) : ''
      },
      personalData: {
        label: 'обработку персональных данных',
        href: personalData?.enable === true ? sanitizeExternalUrl(personalData.link) : ''
      },
      offer: {
        label: 'Офертой',
        href: offer?.enable === true ? sanitizeExternalUrl(offer.link) : ''
      },
      clubRules: {
        label: 'Правилами клуба',
        href: clubRules?.enable === true ? sanitizeExternalUrl(clubRules.link) : ''
      }
    }
  } catch (error) {
    return {
      consent: { label: 'согласие', href: '' },
      personalData: { label: 'обработку персональных данных', href: '' },
      offer: { label: 'Офертой', href: '' },
      clubRules: { label: 'Правилами клуба', href: '' }
    }
  }
}

function clearPhoneError() {
  errorText.value = ''
}

function onCodeInput(index, event) {
  const target = event.target
  const digit = target.value.replace(/\D/g, '').slice(0, 1)
  codeDigits.value[index] = digit
  errorText.value = ''

  if (digit && index < codeDigits.value.length - 1) {
    codeInputRefs.value[index + 1]?.focus()
  }
}

function onCodeBackspace(index, event) {
  if (codeDigits.value[index]) {
    codeDigits.value[index] = ''
    return
  }

  if (index > 0) {
    event.preventDefault()
    codeInputRefs.value[index - 1]?.focus()
    codeDigits.value[index - 1] = ''
  }
}

function onCodePaste(event) {
  event.preventDefault()
  const pastedDigits = event.clipboardData?.getData('text').replace(/\D/g, '').slice(0, 4) || ''

  pastedDigits.split('').forEach((digit, index) => {
    codeDigits.value[index] = digit
  })

  if (pastedDigits.length < 4) {
    for (let index = pastedDigits.length; index < 4; index += 1) {
      codeDigits.value[index] = ''
    }
  }

  const nextIndex = Math.min(pastedDigits.length, 3)
  codeInputRefs.value[nextIndex]?.focus()
}

function startTimer() {
  stopTimer()
  secondsLeft.value = 60

  timerId.value = window.setInterval(() => {
    if (secondsLeft.value <= 1) {
      stopTimer()
      secondsLeft.value = 0
      return
    }

    secondsLeft.value -= 1
  }, 1000)
}

function stopTimer() {
  if (timerId.value !== null) {
    window.clearInterval(timerId.value)
    timerId.value = null
  }
}

function clearCode() {
  codeDigits.value = ['', '', '', '']
}

function clearTemporaryAuth() {
  if (!hasTemporaryAuth.value) {
    return
  }

  clearStoredAuthToken()
  clearStoredAuthPhone()
  pendingToken.value = ''
  hasTemporaryAuth.value = false
}

function resetModal() {
  stopTimer()
  clearTemporaryAuth()
  step.value = 'phone'
  phoneInput.value = ''
  phoneValue.value = ''
  phoneValid.value = false
  currentDialCode.value = ''
  clearCode()
  errorText.value = ''
  isSubmitting.value = false
  secondsLeft.value = 60
  registrationForm.value = {
    name: '',
    surname: '',
    birthDate: null,
    sex: ''
  }
}

function closeModal() {
  emit('close')
}

function disableFutureDates(date) {
  return date >= new Date()
}

async function submitPhone() {
  if (!canSubmitPhone.value || isSubmitting.value) {
    return
  }

  isSubmitting.value = true
  errorText.value = ''

  const response = await BookingAPI.sendSmsLoginCode(phoneValue.value)

  isSubmitting.value = false

  if (response.status === 'code send') {
    step.value = 'code'
    clearCode()
    startTimer()
    await nextTick()
    codeInputRefs.value[0]?.focus()
    return
  }

  errorText.value = response.error || 'Не удалось отправить код'
}

async function resendCode() {
  if (!phoneValue.value || isSubmitting.value) {
    return
  }

  isSubmitting.value = true
  errorText.value = ''

  const response = await BookingAPI.sendSmsLoginCode(phoneValue.value)

  isSubmitting.value = false

  if (response.status === 'code send') {
    clearCode()
    startTimer()
    await nextTick()
    codeInputRefs.value[0]?.focus()
    return
  }

  errorText.value = response.error || 'Не удалось отправить код повторно'
}

async function submitCode() {
  if (!canSubmitCode.value || isSubmitting.value || !phoneValue.value) {
    return
  }

  isSubmitting.value = true
  errorText.value = ''

  const response = await BookingAPI.verifySmsLoginCode(phoneValue.value, codeValue.value)

  isSubmitting.value = false

  if (response.status === 'authorize' && response.token) {
    stopTimer()
    if (response.no_client) {
      pendingToken.value = response.token
      hasTemporaryAuth.value = true
      setStoredAuthToken(response.token)
      setStoredAuthPhone(phoneValue.value)
      registrationForm.value = {
        name: '',
        surname: '',
        birthDate: null,
        sex: ''
      }
      step.value = 'registration'
      return
    }

    emit('authorized', {
      phone: phoneValue.value,
      token: response.token
    })
    return
  }

  errorText.value = response.error || 'Неверный код'
}

async function submitRegistration() {
  if (!canSubmitRegistration.value || isSubmitting.value || !phoneValue.value || !pendingToken.value) {
    return
  }

  isSubmitting.value = true
  errorText.value = ''

  const registrationResult = await BookingAPI.registerUser({
    token: pendingToken.value,
    name: registrationForm.value.name,
    surname: registrationForm.value.surname,
    patronymic: '',
    b_day: registrationForm.value.birthDate,
    sex: registrationForm.value.sex,
    phone: phoneValue.value,
    club: getRuntimeConfig().clubId
  })

  isSubmitting.value = false

  if (!registrationResult?.success || !registrationResult.data?.id) {
    errorText.value = registrationResult?.error || 'Не удалось завершить регистрацию'
    return
  }

  hasTemporaryAuth.value = false
  emit('authorized', {
    phone: phoneValue.value,
    token: pendingToken.value
  })
}
</script>

<style scoped lang="scss">
.fitbase-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.5);
}

.fitbase-modal-content {
  width: min(480px, 100%);
  min-height: 674px;
  box-sizing: border-box;
  padding: 24px 20px 20px;
  background: #FFFFFF;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
}

.fitbase-modal-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.fitbase-modal-title {
  margin: 0;
  color: #252525;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
}

.fitbase-modal-close {
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 7px;
  background: #F3F5F8;
  cursor: pointer;
}

.fitbase-modal-close-icon {
  width: 14px;
  height: 14px;
  display: block;
}

.fitbase-modal-layout {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.fitbase-modal-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fitbase-modal-body {
  width: 100%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  text-align: center;
}

.fitbase-registration-step {
  max-width: 360px;
}

.fitbase-hint-text,
.fitbase-resend-text {
  margin: 0;
  color: #757885;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
}

.fitbase-step-title {
  margin: 0 0 8px;
  color: #252525;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
}

.fitbase-text-input {
  width: 100%;
  height: 50px;
  box-sizing: border-box;
  padding: 17px;
  border: 0;
  border-radius: 14px;
  background: #F9FAFB;
  color: #252525;
  text-align: center;
  font-family: 'Nunito Sans', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  outline: none;
}

.fitbase-phone-input {
  width: 100%;
  height: 50px;
  box-sizing: border-box;
  display: flex;
  align-items: stretch;
  border: 0 !important;
  border-radius: 14px;
  background: #F9FAFB;
  box-shadow: none;
  outline: none;
  overflow: visible;
  text-align: left;
}

.fitbase-phone-input:focus-within {
  box-shadow: none;
  border-color: var(--booking-widget-primary-color, #3C67D6);
}

.fitbase-phone-input :deep(.vti__dropdown) {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 10px;
  background: transparent;
  border-radius: 14px 0 0 14px;
}

.fitbase-phone-input :deep(.vti__dropdown:hover),
.fitbase-phone-input :deep(.vti__dropdown.open) {
  background: #EEF1F5;
}

.fitbase-phone-input :deep(.vti__selection) {
  display: flex;
  align-items: center;
  gap: 2px;
  line-height: 1;
}

.fitbase-phone-input :deep(.vti__flag) {
  display: inline-block;
  flex-shrink: 0;
  margin: 0 4px 0 0;
  vertical-align: middle;
  background-repeat: no-repeat;
}

.fitbase-phone-input :deep(.vti__dropdown-arrow) {
  display: inline-block;
  margin-left: 2px;
  line-height: 1;
  vertical-align: middle;
}

.fitbase-phone-input :deep(.vti__dropdown-list) {
  text-align: left;
  border-radius: 12px;
  z-index: 30;
  top: calc(100% + 4px);
}

.fitbase-phone-input :deep(.vti__input) {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  height: 100%;
  box-sizing: border-box;
  padding: 0 17px 0 8px;
  border: 0 !important;
  border-radius: 0 14px 14px 0;
  background: transparent;
  color: #252525;
  text-align: left;
  font-family: 'Nunito Sans', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 50px;
  outline: none;
}

.fitbase-phone-input :deep(.vti__input::placeholder) {
  color: #9898A5;
  font-weight: 400;
}

.fitbase-text-input::placeholder {
  color: #9898A5;
  font-weight: 400;
}

.fitbase-text-input-left {
  text-align: left;
}

.fitbase-text-input:focus,
.fitbase-code-input:focus {
  border-color: var(--booking-widget-primary-color, #3C67D6);
}

.fitbase-code-hint {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fitbase-code-inputs {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.fitbase-code-input {
  width: 50px;
  height: 50px;
  justify-self: center;
  box-sizing: border-box;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: #F9FAFB;
  color: #252525;
  font-family: 'Nunito Sans', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  outline: none;
}

.fitbase-code-input.filled {
  background: var(--booking-widget-primary-color, #3C67D6);
  color: #FFFFFF;
}

.fitbase-primary-button {
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: var(--booking-widget-primary-color, #3C67D6);
  color: #FFFFFF;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;
  transition: opacity 0.2s ease, background 0.2s ease;
}

.fitbase-primary-button:disabled {
  background: #C7D3F4;
  cursor: not-allowed;
}

.fitbase-error-text {
  margin: 0;
  color: #E25757;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
}

.fitbase-resend-block {
  display: flex;
  justify-content: center;
  min-height: 18px;
}

.fitbase-legal-text {
  margin: 0;
  color: #757885;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
}

.fitbase-legal-text :deep(a) {
  color: var(--booking-widget-primary-color, #3C67D6);
  text-decoration: none;
}

.fitbase-legal-text :deep(a:hover) {
  text-decoration: underline;
}

.fitbase-resend-button {
  padding: 0;
  border: none;
  background: transparent;
  color: var(--booking-widget-primary-color, #3C67D6);
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  cursor: pointer;
}

.fitbase-sex-buttons {
  width: 100%;
  display: flex;
  gap: 8px;
}

.fitbase-sex-button {
  flex: 1 1 0;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: #F3F5F8;
  color: #757885;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.fitbase-sex-button.active {
  background: var(--booking-widget-primary-color, #3C67D6);
  color: #FFFFFF;
}

.fitbase-registration-step :deep(.dp__main) {
  width: 100%;
}

.fitbase-registration-step :deep(.dp__input) {
  width: 100%;
  height: 50px;
  box-sizing: border-box;
  padding: 17px 17px 17px 44px;
  border: 0;
  border-radius: 14px;
  background: #F9FAFB;
  color: #252525;
  font-family: 'Nunito Sans', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
}

.fitbase-registration-step :deep(.dp__input_icon) {
  left: 14px;
}

.fitbase-manufactured-by {
  margin-top: 30px;
  text-align: center;
}

.fitbase-manufactured-by-link {
  display: inline-block;
  line-height: 0;
  text-decoration: none;
}

.fitbase-manufactured-by-image {
  display: block;
  height: 32px;
  width: auto;
}

@media (max-width: 520px) {
  .fitbase-modal-content {
    min-height: auto;
  }

  .fitbase-modal-main {
    align-items: flex-start;
  }

  .fitbase-manufactured-by {
    margin-top: 24px;
  }
}
</style>
